import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import api, { apiErrorText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Link2, Plus, RefreshCw, Pencil, Search, Settings2, X } from "lucide-react";
import { toast } from "sonner";

const fmtUSDT = (n) =>
  Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 6 });

const todayISO = () => new Date().toISOString().slice(0, 10);
const monthStartISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const short = (s) => (s ? `${s.slice(0, 6)}...${s.slice(-6)}` : "-");

export default function BscUSDT() {
  const [addresses, setAddresses] = useState([]);
  const [data, setData] = useState({ items: [], summary: {} });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filters, setFilters] = useState({
    from: monthStartISO(),
    to: todayISO(),
    direction: "all",
    category: "all",
    q: "",
  });
  const [addressOpen, setAddressOpen] = useState(false);
  const [addressForm, setAddressForm] = useState({ address: "", label: "", notes: "" });
  const [labelFor, setLabelFor] = useState(null);
  const [labelForm, setLabelForm] = useState({ label: "", category: "", notes: "" });
  const [catOpen, setCatOpen] = useState(false);
  const [catDraft, setCatDraft] = useState([]);
  const [catInput, setCatInput] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      if (filters.direction !== "all") params.direction = filters.direction;
      if (filters.category !== "all") params.category = filters.category;
      if (filters.q.trim()) params.q = filters.q.trim();
      const [a, t, cats] = await Promise.all([
        api.get("/bsc/addresses"),
        api.get("/bsc/transactions", { params }),
        api.get("/usdt-tracker/categories"),
      ]);
      setAddresses(a.data);
      setData(t.data);
      setCategories(cats.data.categories || []);
    } catch (e) {
      toast.error(apiErrorText(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(); // eslint-disable-next-line
  }, [filters.from, filters.to, filters.direction, filters.category, filters.q]);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/bsc/sync");
      const inserted = (r.data.results || []).reduce((s, x) => s + (x.inserted || 0), 0);
      toast.success(`BSC synced · ${inserted} new tx`);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    } finally {
      setSyncing(false);
    }
  };

  const addAddress = async () => {
    try {
      await api.post("/bsc/addresses", addressForm);
      toast.success("Address added");
      setAddressOpen(false);
      setAddressForm({ address: "", label: "", notes: "" });
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const openLabel = (tx) => {
    setLabelFor(tx);
    setLabelForm({ label: tx.label || "", category: tx.category || "", notes: tx.notes || "" });
  };

  const saveLabel = async () => {
    try {
      await api.put(`/bsc/transactions/${encodeURIComponent(labelFor.id)}`, labelForm);
      toast.success("Label saved");
      setLabelFor(null);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const openCats = () => {
    setCatDraft([...categories]);
    setCatInput("");
    setCatOpen(true);
  };

  const saveCats = async () => {
    try {
      await api.put("/usdt-tracker/categories", { categories: catDraft });
      toast.success("Categories updated");
      setCatOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const uniqueLabels = useMemo(() => {
    return [...new Set((data.items || []).map((x) => x.label).filter(Boolean))];
  }, [data.items]);

  const sortedCategories = useMemo(() => {
    return Object.entries(data.summary?.by_category || {}).sort(
      (a, b) => (b[1].sent_usdt || 0) - (a[1].sent_usdt || 0)
    );
  }, [data.summary]);

  return (
    <Layout
      title="BSC USDT"
      subtitle="Read-only BEP20 USDT tracker for sent, received, net flow, and categories"
      testId="bsc-usdt-page"
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={openCats} className="h-9">
            <Settings2 size={14} className="mr-1.5" /> Categories
          </Button>
          <Button variant="outline" onClick={() => setAddressOpen(true)} className="h-9">
            <Plus size={14} className="mr-1.5" /> Address
          </Button>
          <Button
            onClick={sync}
            disabled={syncing}
            className="h-9 bg-zinc-900 hover:bg-zinc-800 text-white"
            data-testid="bsc-sync-btn"
          >
            <RefreshCw size={14} className={`mr-1.5 ${syncing ? "animate-spin" : ""}`} />
            Sync
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3 mb-5">
        <div className="stat-card p-4">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">Received</div>
          <div className="font-mono text-[22px] font-medium mt-1 text-emerald-700">
            {fmtUSDT(data.summary?.received_usdt)} USDT
          </div>
        </div>
        <div className="stat-card p-4">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">Sent</div>
          <div className="font-mono text-[22px] font-medium mt-1 text-rose-700">
            {fmtUSDT(data.summary?.sent_usdt)} USDT
          </div>
        </div>
        <div className="stat-card p-4">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">Net</div>
          <div className="font-mono text-[22px] font-medium mt-1">
            {fmtUSDT(data.summary?.net_usdt)} USDT
          </div>
        </div>
        <div className="stat-card p-4">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
            Available balance
          </div>
          <div className="font-mono text-[22px] font-medium mt-1 text-sky-700">
            {fmtUSDT(data.summary?.available_balance_usdt)} USDT
          </div>
        </div>
        <div className="stat-card p-4">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">Tx count</div>
          <div className="font-mono text-[22px] font-medium mt-1">{data.summary?.count || 0}</div>
        </div>
      </div>

      <div className="stat-card p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            className="h-9 pl-8"
            placeholder="Search tx, address, label"
          />
        </div>
        <Input
          type="date"
          value={filters.from}
          onChange={(e) => setFilters({ ...filters, from: e.target.value })}
          className="h-9 w-[150px]"
        />
        <Input
          type="date"
          value={filters.to}
          onChange={(e) => setFilters({ ...filters, to: e.target.value })}
          className="h-9 w-[150px]"
        />
        <Select
          value={filters.direction}
          onValueChange={(v) => setFilters({ ...filters, direction: v })}
        >
          <SelectTrigger className="h-9 w-[150px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All flow</SelectItem>
            <SelectItem value="received">Received</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={filters.category}
          onValueChange={(v) => setFilters({ ...filters, category: v })}
        >
          <SelectTrigger className="h-9 w-[170px]">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            <SelectItem value="uncategorized">Uncategorized</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="stat-card p-3 mb-4">
        <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">
          Tracked addresses
        </div>
        <div className="flex flex-wrap gap-1.5">
          {addresses.map((a) => (
            <span
              key={a.address}
              className="inline-flex items-center gap-1.5 text-[12px] border hairline rounded px-2 py-1 bg-white"
              title={a.address}
            >
              <Link2 size={12} /> {a.label || short(a.address)}
              <span className="font-mono text-zinc-400">{short(a.address)}</span>
            </span>
          ))}
        </div>
      </div>

      {sortedCategories.length > 0 && (
        <div className="stat-card p-3 mb-4">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">
            Spending by category
          </div>
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-2">
            {sortedCategories.slice(0, 8).map(([name, info]) => (
              <button
                key={name}
                onClick={() => name !== "Uncategorized" && setFilters({ ...filters, category: name })}
                className="text-left rounded border hairline bg-white px-3 py-2 hover:bg-zinc-50"
              >
                <div className="text-[12px] font-medium truncate">{name}</div>
                <div className="font-mono text-[15px] text-rose-700">
                  {fmtUSDT(info.sent_usdt)} USDT
                </div>
                <div className="text-[11px] text-zinc-500">
                  received {fmtUSDT(info.received_usdt)} · {info.count} tx
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {uniqueLabels.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-1.5">
          {uniqueLabels.map((label) => (
            <button
              key={label}
              onClick={() => setFilters({ ...filters, q: label })}
              className="text-[12px] rounded border hairline bg-white px-2 py-1 hover:bg-zinc-50"
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="stat-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-500 border-b hairline bg-zinc-50/50">
                <th className="py-3 px-4 font-medium">Date</th>
                <th className="py-3 px-4 font-medium">Flow</th>
                <th className="py-3 px-4 font-medium text-right">USDT</th>
                <th className="py-3 px-4 font-medium">Counterparty</th>
                <th className="py-3 px-4 font-medium">Category</th>
                <th className="py-3 px-4 font-medium">Label</th>
                <th className="py-3 px-4 font-medium">Tx</th>
                <th className="py-3 px-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y hairline">
              {loading ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-zinc-500">Loading...</td>
                </tr>
              ) : data.items?.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-zinc-500">
                    No synced BSC USDT transactions yet. Click Sync.
                  </td>
                </tr>
              ) : (
                data.items.map((tx) => (
                  <tr key={tx.id} className="row-hover">
                    <td className="py-3 px-4 font-mono text-[12px]">{tx.date}</td>
                    <td className="py-3 px-4">
                      <span
                        className={`text-[11px] px-1.5 py-0.5 rounded border capitalize ${
                          tx.direction === "received"
                            ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                            : "bg-rose-50 border-rose-200 text-rose-700"
                        }`}
                      >
                        {tx.direction}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-mono text-right">
                      {fmtUSDT(tx.amount_usdt)}
                    </td>
                    <td className="py-3 px-4 font-mono text-[12px]" title={tx.counterparty}>
                      {short(tx.counterparty)}
                    </td>
                    <td className="py-3 px-4">
                      {tx.category ? (
                        <span className="text-[11px] px-1.5 py-0.5 rounded border bg-amber-50 border-amber-200 text-amber-800">
                          {tx.category}
                        </span>
                      ) : (
                        <span className="text-zinc-400">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {tx.label ? (
                        <span className="text-[11px] px-1.5 py-0.5 rounded border bg-sky-50 border-sky-200 text-sky-800">
                          {tx.label}
                        </span>
                      ) : (
                        <span className="text-zinc-400">-</span>
                      )}
                      {tx.notes && <div className="text-[11px] text-zinc-500 mt-0.5">{tx.notes}</div>}
                    </td>
                    <td className="py-3 px-4 font-mono text-[12px]" title={tx.txid}>
                      {short(tx.txid)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => openLabel(tx)}
                        className="p-1.5 rounded hover:bg-zinc-100 text-zinc-600"
                        title="Edit label"
                      >
                        <Pencil size={14} strokeWidth={1.6} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={addressOpen} onOpenChange={setAddressOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Track BSC address</DialogTitle>
            <DialogDescription>Add a public BSC address. No private key needed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Address</Label>
              <Input
                value={addressForm.address}
                onChange={(e) => setAddressForm({ ...addressForm, address: e.target.value })}
                placeholder="0x..."
              />
            </div>
            <div className="space-y-1.5">
              <Label>Label</Label>
              <Input
                value={addressForm.label}
                onChange={(e) => setAddressForm({ ...addressForm, label: e.target.value })}
                placeholder="Main wallet"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input
                value={addressForm.notes}
                onChange={(e) => setAddressForm({ ...addressForm, notes: e.target.value })}
                placeholder="Optional"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddressOpen(false)}>Cancel</Button>
            <Button onClick={addAddress} className="bg-zinc-900 hover:bg-zinc-800 text-white">
              Add address
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!labelFor} onOpenChange={(v) => !v && setLabelFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Label transaction</DialogTitle>
            <DialogDescription>Add category for spend tracking and keep labels for extra details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select
                value={labelForm.category || "__none"}
                onValueChange={(v) => setLabelForm({ ...labelForm, category: v === "__none" ? "" : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">No category</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Label</Label>
              <Input
                value={labelForm.label}
                onChange={(e) => setLabelForm({ ...labelForm, label: e.target.value })}
                placeholder="e.g. Exchange"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input
                value={labelForm.notes}
                onChange={(e) => setLabelForm({ ...labelForm, notes: e.target.value })}
                placeholder="Optional"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLabelFor(null)}>Cancel</Button>
            <Button onClick={saveLabel} className="bg-zinc-900 hover:bg-zinc-800 text-white">
              Save transaction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Tracker categories</DialogTitle>
            <DialogDescription>These categories are shared by TRON and BSC USDT trackers.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                value={catInput}
                onChange={(e) => setCatInput(e.target.value)}
                placeholder="Add category"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const v = catInput.trim();
                    if (v && !catDraft.some((c) => c.toLowerCase() === v.toLowerCase())) {
                      setCatDraft([...catDraft, v]);
                    }
                    setCatInput("");
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const v = catInput.trim();
                  if (v && !catDraft.some((c) => c.toLowerCase() === v.toLowerCase())) {
                    setCatDraft([...catDraft, v]);
                  }
                  setCatInput("");
                }}
              >
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {catDraft.map((c) => (
                <span key={c} className="inline-flex items-center gap-1 rounded border hairline px-2 py-1 text-[12px] bg-white">
                  {c}
                  <button onClick={() => setCatDraft(catDraft.filter((x) => x !== c))}>
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatOpen(false)}>Cancel</Button>
            <Button onClick={saveCats} className="bg-zinc-900 hover:bg-zinc-800 text-white">
              Save categories
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
